/* eslint-disable no-param-reassign */
import {
  BaseEditor,
  Descendant,
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  Range,
  Transforms,
  Path,
} from 'slate';
import { HistoryEditor } from 'slate-history';
import { ReactEditor } from 'slate-react';

export type FontSize = 'small' | 'normal' | 'medium' | 'huge';

type FontSizedElement = { fontSize?: FontSize };

type ParagraphElement = {
  type: 'paragraph';
  children: CustomText[];
} & FontSizedElement;
type HeadingOneElement = {
  type: 'heading-one';
  children: CustomText[];
} & FontSizedElement;
type HeadingTwoElement = {
  type: 'heading-two';
  children: CustomText[];
} & FontSizedElement;
type BulletedListElement = {
  type: 'unorderedList';
  children: CustomElement[];
} & FontSizedElement;
type NumberedListElement = {
  type: 'orderedList';
  children: CustomElement[];
} & FontSizedElement;
type ListItemElement = {
  type: 'list-item';
  children: CustomText[];
} & FontSizedElement;
type LinkElement = {
  type: 'link';
  url: string;
  openInNewTab?: boolean;
  children: CustomText[];
};

type CustomElement =
  | ParagraphElement
  | HeadingOneElement
  | HeadingTwoElement
  | BulletedListElement
  | NumberedListElement
  | ListItemElement
  | LinkElement;

type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
};

export type RichTextValue = Descendant[];
export type MarkFormat = 'bold' | 'italic' | 'underline';
export type BlockFormat =
  | 'paragraph'
  | 'heading-one'
  | 'heading-two'
  | 'unorderedList'
  | 'orderedList'
  | 'list-item'
  | 'link';

const LIST_TYPES: BlockFormat[] = ['orderedList', 'unorderedList'];

export const createInitialValue = (): RichTextValue => [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export const normalizeRichTextValue = (value: unknown): RichTextValue => {
  if (Array.isArray(value) && value.length > 0) {
    return value as RichTextValue;
  }

  if (typeof value === 'string' && value.trim()) {
    return [
      {
        type: 'paragraph',
        children: [{ text: value.trim() }],
      },
    ];
  }

  return createInitialValue();
};

export const richTextToPlainText = (value: unknown): string =>
  normalizeRichTextValue(value)
    .map((node) => SlateNode.string(node as Descendant))
    .join('\n')
    .trim();

export const isMarkActive = (editor: Editor, format: MarkFormat) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleMark = (editor: Editor, format: MarkFormat) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
    return;
  }
  Editor.addMark(editor, format, true);
};

export const isBlockActive = (
  editor: Editor,
  format: BlockFormat,
  blockType = 'type',
) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (node) =>
        !Editor.isEditor(node) &&
        SlateElement.isElement(node) &&
        (node as SlateElement & { type?: string })[
          blockType as keyof SlateElement
        ] === format,
    }),
  );

  return Boolean(match);
};

const isListType = (type?: string): type is BlockFormat =>
  !!type && LIST_TYPES.includes(type as BlockFormat);

export const toggleBlock = (editor: Editor, format: BlockFormat) => {
  const { selection } = editor;
  if (!selection) return;

  const isList = LIST_TYPES.includes(format);

  // ------------------------------------------------------------------
  // 1. SPECIAL CASE: cursor inside a single list-item (collapsed)
  //    -> un-bullet just that item
  //    - if list has 1 item: list -> paragraph
  //    - if list has many: only this item -> paragraph (others stay bullets)
  // ------------------------------------------------------------------
  if (isList && Range.isCollapsed(selection)) {
    const [listItemEntry] = Editor.nodes(editor, {
      at: selection,
      match: (node) =>
        !Editor.isEditor(node) &&
        SlateElement.isElement(node) &&
        (node as SlateElement & { type?: string }).type === 'list-item',
    });

    if (listItemEntry) {
      const [, listItemPath] = listItemEntry as [
        SlateElement & { type?: string; children: Descendant[] },
        Path,
      ];

      const [parentNode, parentPath] = Editor.parent(editor, listItemPath) as [
        SlateElement & { type?: string; children: Descendant[] },
        Path,
      ];

      const parentType = parentNode.type;

      if (isListType(parentType)) {
        const listLength = parentNode.children.length ?? 0;

        Editor.withoutNormalizing(editor, () => {
          if (listLength <= 1) {
            // Only one <li> in this list:
            //  - make it a paragraph
            //  - unwrap the list container
            Transforms.setNodes(
              editor,
              { type: 'paragraph' as BlockFormat },
              { at: listItemPath },
            );

            Transforms.unwrapNodes(editor, {
              at: parentPath,
              match: (node) =>
                !Editor.isEditor(node) &&
                SlateElement.isElement(node) &&
                isListType((node as SlateElement & { type?: string }).type),
            });
          } else {
            // Multiple <li> siblings:
            //  - only THIS one becomes a paragraph and is lifted out
            Transforms.setNodes(
              editor,
              { type: 'paragraph' as BlockFormat },
              { at: listItemPath },
            );
            Transforms.liftNodes(editor, { at: listItemPath });
          }
        });

        return; // important: don't fall through
      }
    }
  }

  // ------------------------------------------------------------------
  // 2. GENERAL CASE (ranges, mixed bullets/paras, cursor not in <li>)
  //    Standard Slate list toggle:
  //    - if any list container of this format in selection -> everything in
  //      selection becomes paragraphs (your "any bullet -> all para").
  //    - otherwise, all selected blocks become list-items inside one list.
  // ------------------------------------------------------------------

  const isActive = isBlockActive(editor, format); // your existing helper

  Editor.withoutNormalizing(editor, () => {
    // Unwrap any list containers (unorderedList / orderedList) in selection
    Transforms.unwrapNodes(editor, {
      match: (node) =>
        !Editor.isEditor(node) &&
        SlateElement.isElement(node) &&
        isListType((node as SlateElement & { type?: string }).type),
      split: true,
    });
    let newType: BlockFormat = 'paragraph';
    if (isActive) {
      newType = 'paragraph';
    } else if (isList) {
      newType = 'list-item';
    } else {
      newType = format;
    }

    // Set all selected blocks to newType
    Transforms.setNodes<SlateElement>(
      editor,
      { type: newType },
      {
        match: (node) =>
          !Editor.isEditor(node) &&
          SlateElement.isElement(node) &&
          Editor.isBlock(editor, node),
      },
    );

    // Turning list ON for non-active selection: wrap all blocks in a list
    if (!isActive && isList) {
      const list: SlateElement = {
        type: format,
        children: [],
      } as SlateElement;

      Transforms.wrapNodes(editor, list, {
        match: (node) =>
          !Editor.isEditor(node) &&
          SlateElement.isElement(node) &&
          Editor.isBlock(editor, node),
      });
    }
  });
};

export const isLinkActive = (editor: Editor) =>
  isBlockActive(editor, 'link', 'type');

export const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (node) =>
      !Editor.isEditor(node) &&
      SlateElement.isElement(node) &&
      (node as SlateElement & { type?: string }).type === 'link',
  });
};

export const wrapLink = (
  editor: Editor,
  url: string,
  options: { openInNewTab?: boolean } = {},
) => {
  const normalized = normalizeUrl(url);
  if (!normalized) return;

  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: LinkElement = {
    type: 'link',
    url: normalized,
    openInNewTab: options.openInNewTab,
    children: isCollapsed ? [{ text: normalized }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const insertLink = (
  editor: Editor,
  url: string,
  options: { openInNewTab?: boolean } = {},
) => {
  if (!url) return;
  wrapLink(editor, url, options);
};

export const withLinks = <T extends BaseEditor & ReactEditor & HistoryEditor>(
  editor: T,
) => {
  const { insertData, insertText, isInline } = editor;

  editor.isInline = (element) =>
    element.type === 'link' ? true : isInline(element);

  editor.insertText = (text) => {
    if (shouldAutoLink(text)) {
      const normalized = normalizeUrl(text);
      if (normalized) {
        wrapLink(editor, normalized);
        return;
      }
    }
    insertText(text);
  };

  editor.insertData = (data) => {
    const text = data.getData('text/plain');

    if (shouldAutoLink(text)) {
      const normalized = normalizeUrl(text);
      if (normalized) {
        wrapLink(editor, normalized);
        return;
      }
    }

    insertData(data);
  };

  return editor;
};

export const isValidUrl = (maybeUrl: string) => {
  return Boolean(normalizeUrl(maybeUrl));
};

const shouldAutoLink = (maybeUrl: string) => {
  if (!maybeUrl || maybeUrl.includes(' ')) return false;
  const hasProtocol =
    maybeUrl.startsWith('http://') || maybeUrl.startsWith('https://');
  const looksLikeDomain =
    maybeUrl.includes('.') && !maybeUrl.endsWith('.') && maybeUrl.length > 5;
  return hasProtocol || looksLikeDomain;
};

const normalizeUrl = (maybeUrl: string) => {
  try {
    const url = new URL(maybeUrl);
    return url.toString();
  } catch (error) {
    try {
      const urlWithProtocol = new URL(`https://${maybeUrl}`);
      return urlWithProtocol.toString();
    } catch (innerError) {
      return '';
    }
  }
};

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const deserializeHtml = (el: Node, marks: Marks = {}): Descendant[] => {
  // Text node
  if (el.nodeType === Node.TEXT_NODE) {
    const text = el.textContent ?? '';
    if (text.trim() === '') return [];
    return [{ text, ...marks }];
  }

  if (!(el instanceof HTMLElement)) {
    return [];
  }

  // Update marks based on this element
  const nextMarks: Marks = { ...marks };

  const tag = el.nodeName.toUpperCase();
  const { style } = el;

  // Marks from tags
  if (tag === 'B' || tag === 'STRONG') nextMarks.bold = true;
  if (tag === 'I' || tag === 'EM') nextMarks.italic = true;
  if (tag === 'U') nextMarks.underline = true;

  // Marks from inline styles (for pasted from Word etc.)
  if (style.fontWeight && +style.fontWeight >= 600) nextMarks.bold = true;
  if (style.fontStyle === 'italic') nextMarks.italic = true;
  if (style.textDecoration.includes('underline')) nextMarks.underline = true;

  // Helper to deserialize children with updated marks
  const children: Descendant[] = Array.from(el.childNodes).flatMap((child) =>
    deserializeHtml(child, nextMarks),
  );

  if (children.length === 0) {
    // Avoid empty elements – Slate hates empty blocks without text
    return [{ text: '', ...nextMarks }];
  }

  switch (tag) {
    // -------- BLOCKS --------
    case 'P':
    case 'DIV':
      return [
        {
          type: 'paragraph',
          children,
        } as ParagraphElement,
      ];

    case 'H1':
      return [
        {
          type: 'paragraph',
          children,
        } as ParagraphElement,
      ];
    case 'H2':
      return [
        {
          type: 'paragraph',
          children,
        } as ParagraphElement,
      ];
    // add H3/H4 mapping if you have them

    case 'UL':
      return [
        {
          type: 'unorderedList',
          children: normalizeListChildren(children),
        } as BulletedListElement,
      ];

    case 'OL':
      return [
        {
          type: 'orderedList',
          children: normalizeListChildren(children),
        } as NumberedListElement,
      ];

    case 'LI':
      return [
        {
          type: 'list-item',
          children,
        } as ListItemElement,
      ];

    // -------- INLINE --------
    case 'A': {
      const href = el.getAttribute('href') ?? '';
      return [
        {
          type: 'link',
          url: href,
          children,
        } as LinkElement,
      ];
    }

    default:
      return [
        {
          type: 'paragraph',
          children,
        } as ParagraphElement,
      ];
  }
};

/**
 * Ensure children of UL/OL are list-item elements.
 * (Sometimes pasted HTML has <p> or raw text inside <ul>.)
 */
const normalizeListChildren = (children: Descendant[]): Descendant[] => {
  return children.map((child) => {
    if (SlateElement.isElement(child) && child.type === 'list-item') {
      return child;
    }
    // Wrap any non-list block in a list-item
    return {
      type: 'list-item',
      children: [child as ListItemElement['children'][number]],
    } as ListItemElement;
  });
};

type Marks = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
};

export const withHtmlPaste = <T extends Editor>(editor: T): T => {
  const { insertData, isInline } = editor;

  // Tell Slate that links are inline
  editor.isInline = (element) =>
    (SlateElement.isElement(element) && element.type === 'link') ||
    isInline(element);

  editor.insertData = (data) => {
    const html = data.getData('text/html');

    if (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const fragment = deserializeHtml(doc.body);
      if (fragment.length > 0) {
        Transforms.insertFragment(editor, fragment);
        return;
      }
    }

    // fallback (plain text, files, etc.)
    insertData(data);
  };

  return editor;
};
